import React, { useState, useEffect } from "react";
import {
  SortableContainer as sortableContainer,
  SortableElement as sortableElement,
} from "react-sortable-hoc";
import arrayMove from "array-move";
import { TableRow, TableCell, IconButton, TableBody } from "@mui/material";
import { makeStyles } from "@mui/styles";
import SortIcon from "@mui/icons-material/Sort";
import AddBoxIcon from "@mui/icons-material/AddBox";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import ListSortable from "../coberturasSelector/ListSortable";
import CoberturasSelector from "../coberturasSelector/CoberturasSelector";
import { EsquemaRow, IdCobertura, SortEnd } from "../types";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    backgroundColor: theme.colours.white,
    overflow: "visible",
  },
  table: {},
  tableCell: {
    padding: "0px 0px 0px 20px",
    fontSize: "12px",
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.33",
    letterSpacing: "normal",
    textAlign: "left",
  },
  tableCellHeader: {
    fontSize: "small",
    paddingRight: 0,
    paddingTop: 10,
    paddingBottom: 10,
    fontWeight: "bold",
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.33",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.violeta,
  },
  headers: {
    justifyContent: "center",
  },
  rowTable: {
    fontSize: "1.2rem",
  },
  iconButton: {
    color: theme.colours.brownGrey2,
    padding: 0,
    paddingRight: 15,
  },
  coberturasContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row" as const,
    justifyContent: "space-between",
  },
  dash: { display: "flex", justifyContent: "center" },
}));

interface SortableItemInnerProps {
  row: EsquemaRow;
  onChange: (items: IdCobertura[] | null) => void;
  onClickCobertura: (idCompania: string, cobertura: IdCobertura) => void;
  sortMode: boolean;
  onSortMode: (value: boolean) => () => void;
}

const SortableItem = sortableElement(
  ({
    row,
    onChange,
    onClickCobertura,
    sortMode,
    onSortMode,
  }: SortableItemInnerProps) => {
    const classes = useStyles();

    return (
      <TableRow className={classes.rowTable} key={row.idCompania}>
        <TableCell className={classes.tableCell}>
          {row.orden + ". " + row.idCompania}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {sortMode && (
            <div className={classes.coberturasContainer}>
              <ListSortable
                value={row}
                onChange={onChange}
                onClickCobertura={onClickCobertura}
              />
              <IconButton
                className={classes.iconButton}
                onClick={onSortMode(!sortMode)}
                size="small"
              >
                <AddBoxIcon />
              </IconButton>
            </div>
          )}
          {!sortMode && (
            <div className={classes.coberturasContainer}>
              <CoberturasSelector
                value={row}
                idsCoberturas={row.idsCoberturas}
                onChangeCoberturas={onChange}
              />
              <IconButton
                className={classes.iconButton}
                onClick={onSortMode(!sortMode)}
                size="small"
              >
                <SortIcon />
              </IconButton>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  },
);

interface EsquemasSortableTableProps {
  esquemas: EsquemaRow[];
  onChangeOrder?: (items: EsquemaRow[]) => void;
  onChangeCoberturas: (
    row: EsquemaRow,
  ) => (items: IdCobertura[] | null) => void;
  onClickCobertura: (idCompania: string, cobertura: IdCobertura) => void;
}

const EsquemasSortableTable: React.FC<EsquemasSortableTableProps> = ({
  esquemas,
  onChangeOrder,
  onChangeCoberturas,
  onClickCobertura,
}) => {
  const [items, setItems] = useState<EsquemaRow[]>(esquemas);
  const [sortMode, setSortMode] = useState(true);

  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      const newItems: EsquemaRow[] = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      if (onChangeOrder) onChangeOrder(newItems);
    }
  };

  useEffect(() => {
    setItems(esquemas);
  }, [esquemas]);

  const handleSortMode = (value: boolean) => () => {
    setSortMode(value);
  };

  const classes = useStyles();

  return (
    <SortableContainer pressDelay={200} onSortEnd={onSortEnd}>
      <div className={classes.tableContainer}>
        <Table size="small" className={classes.table}>
          <TableHead className={classes.headers}>
            <TableRow>
              <TableCell className={classes.tableCellHeader}>
                Compañia
              </TableCell>
              <TableCell className={classes.tableCellHeader}>
                Ids Coberturas
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row, index) => (
              <SortableItem
                key={`item-${row.idCompania}`}
                index={index}
                row={row}
                onClickCobertura={onClickCobertura}
                onChange={onChangeCoberturas(row)}
                onSortMode={handleSortMode}
                sortMode={sortMode}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </SortableContainer>
  );
};

interface SortableContainerInnerProps {
  children: React.ReactNode;
}

const SortableContainer = sortableContainer(
  ({ children }: SortableContainerInnerProps) => {
    return <ul style={{ margin: 0, padding: 0 }}>{children}</ul>;
  },
);

export default EsquemasSortableTable;
